import { useMemo, useRef, useState } from "react";
import { DirectUpload } from "@rails/activestorage";
import { useQueryClient } from "react-query";
import {
  activeStorageBlobUrl,
  directUploadPath,
} from "@circle-react/helpers/urlHelpers";
import { ATTACHMENT_STATUS_COMPLETED } from "../constants";
import {
  ATTACHMENT_STATUS_FAILED,
  ATTACHMENT_STATUS_UPLOADING,
} from "../constants";
import type { Attachment } from "../types/attachment";
import { useCreateAttachment } from "./useCreateAttachment";
import { useDeleteAttachment } from "./useDeleteAttachment";
import { useListAttachments } from "./useListAttachments";

interface AttachmentUploadParams {
  file: File;
  filename: string;
  url: string;
}

interface StoredAttachment {
  attachmentId?: string;
  blobUrl?: string;
  completed: boolean;
  filename: string;
  progress?: number;
  status?:
    | typeof ATTACHMENT_STATUS_COMPLETED
    | typeof ATTACHMENT_STATUS_FAILED
    | typeof ATTACHMENT_STATUS_UPLOADING;
  url: string;
}

export const useAttachmentsUpload = ({
  conversationId,
}: {
  conversationId: string;
}) => {
  const [attachments, setAttachments] = useState<StoredAttachment[]>(() => []);
  const canceledUploads = useRef<Record<string, boolean>>({});

  // Fetch existing attachments from backend
  const { data: uploadedAttachments } = useListAttachments({ conversationId });

  // Combine local upload state with existing attachments
  const allAttachments = useMemo(() => {
    const existingMapped = (uploadedAttachments?.attachments || []).map(
      (attachment: Attachment) => ({
        url: attachment.url,
        filename: attachment.filename,
        completed: attachment.completed,
        attachmentId: attachment.id,
        status: ATTACHMENT_STATUS_COMPLETED,
      }),
    );

    // Only include local attachments that aren't already on server
    const localOnly = attachments.filter(
      localAttachment =>
        !existingMapped.some(
          serverAttachment =>
            serverAttachment.attachmentId === localAttachment.attachmentId,
        ),
    );

    return [...existingMapped, ...localOnly];
  }, [attachments, uploadedAttachments?.attachments]);

  const updateAttachment = (url: string, props: Partial<StoredAttachment>) => {
    setAttachments((prevAttachments: StoredAttachment[]) =>
      prevAttachments.map(attachment => {
        if (attachment.url === url) {
          return { ...attachment, ...props };
        }
        return attachment;
      }),
    );
  };

  const { mutateAsync: deleteAttachment } = useDeleteAttachment({
    onSuccess: () => {},
    onError: (error: unknown) => {
      console.error("Failed to delete attachment:", error);
    },
  });

  const queryClient = useQueryClient();

  const cancelAttachment = (attachmentUrl: string) => {
    canceledUploads.current[attachmentUrl] = true;

    const attachment = allAttachments.find(att => att.url === attachmentUrl);

    if (attachment?.attachmentId) {
      deleteAttachment({
        conversationId,
        attachmentId: attachment.attachmentId,
      })
        .then(() => {
          void queryClient.invalidateQueries(["attachments", conversationId]);
        })
        .catch((error: unknown) => {
          console.error("Failed to delete attachment from backend:", error);
        });
    }

    setAttachments((prevAttachments: StoredAttachment[]) =>
      prevAttachments.filter(
        (elem: StoredAttachment) => elem.url !== attachmentUrl,
      ),
    );
  };

  const { mutateAsync: createAttachment } = useCreateAttachment({
    conversationId,
  });

  const startUpload = ({ url, file, filename }: AttachmentUploadParams) => {
    updateAttachment(url, {
      status: ATTACHMENT_STATUS_UPLOADING,
    });

    const upload = new DirectUpload(file, directUploadPath(), {
      directUploadWillStoreFileWithXHR: xhr =>
        xhr.upload.addEventListener("progress", e => {
          if (canceledUploads.current[url]) {
            xhr.abort();
          }
          updateAttachment(url, {
            progress: (e.loaded / e.total) * 100,
          });
        }),
    });

    upload.create((error, blob) => {
      if (error) {
        updateAttachment(url, {
          status: ATTACHMENT_STATUS_FAILED,
        });
        throw new Error(String(error));
      } else {
        createAttachment({ signedId: blob.signed_id, filename, url })
          .then(json => {
            const blobUrl = activeStorageBlobUrl({
              signed_id: blob.signed_id,
              filename: blob.filename,
            });
            // Use the attachment ID from the response
            const attachmentId = (json as any).id;

            updateAttachment(url, {
              status: ATTACHMENT_STATUS_COMPLETED,
              attachmentId,
              blobUrl,
              completed: true,
            });
          })
          .catch(error => {
            console.error(error);
          });
      }
    });
  };

  const uploadAttachments = (files: File[]) => {
    const fileObjects = files.map((file: File) => ({
      file,
      url: URL.createObjectURL(file),
      filename: file.name,
      completed: false,
    }));

    setAttachments([...attachments, ...fileObjects]);

    for (const fileObject of fileObjects) {
      startUpload({
        url: fileObject.url,
        file: fileObject.file,
        filename: fileObject.filename,
      });
    }
  };

  return {
    uploadAttachments,
    attachments: allAttachments,
    setAttachments,
    cancelAttachment,
  };
};
